import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAnalytics } from 'core/analytics/hooks';
import {
  useDispatch,
  useLogger,
  usePostMessageToFunnelBuilderOnRouteChange,
  useRedirectToAskNebula,
} from 'core/common/hooks';
import { useObservability } from 'core/common/observability/context';
import { ScreenAttributes } from 'core/flow/entities';
import {
  useEmailFlow,
  useFlowAttributes,
  useFlowConditions,
  useFlowContent,
  useFlowNavigation,
  useFlowOffers,
} from 'core/flow/hooks';
import { OffersByCategory } from 'core/offers/entities';
import { useFetchOffers } from 'core/offers/hooks';
import { UserProfile, UserResponse, UserSettings } from 'core/user/entities';
import { useUser, useUserSettings } from 'core/user/hooks';
import { createOrder } from 'core/user/store';
import { useFunnelData } from './FunnelContext';

export type QuizContextData = {
  getScreenAttributes: (id: string) => ScreenAttributes;
  userPreferences: UserProfile;
  completeQuiz: (email: string, productName: string) => Promise<void>;
  updateUserSettings: (data: UserSettings) => void;
  updateUserEmailConsent: (emailConsent: boolean) => void;
  extraNext: (id: string) => void;
  next: (args: NextArgs) => Promise<string>;
  back: (id: string) => void;
  link: string;
  offers: OffersByCategory;
  signIn: () => void;
  flowInitialScreenId: string;
  updateDynamicAttributes: (dataKey: string, pageId: string) => void;
  userResponses: Record<string, UserResponse>;
  updateUserData: (dataKey: string, data: UserResponse) => void;
  fetchOffers: () => void;
};

export const QuizContext = createContext<QuizContextData>(undefined!);

export type FlowProviderProps = {
  children?: ReactNode;
  variant?: 'default' | 'new';
};

export type NextArgs = {
  id: string;
  value?: string | string[];
};

export const FlowProvider = ({ children }: FlowProviderProps) => {
  const dispatch = useDispatch();
  const logger = useLogger();

  const { link, name, flow, analyticsIds } = useFunnelData();

  const { transformContentToTemplate } = useFlowContent();

  const { getAttributesByCondition, getAttributesByFeature } = useFlowConditions();

  const { getScreenAttributes, updateDynamicAttributes } = useFlowAttributes({
    screens: flow.screens,
    flowInitialScreenId: flow.initialScreen,
    dynamicUserProfile: flow.attributes.dynamicUserProfile,
    getAttributesByFeature,
    getAttributesByCondition,
    transformContentToTemplate,
  });

  const { goNext, goExtraNext, goBack } = useFlowNavigation({
    rootFunnelLink: link,
    flowInitialScreenId: flow.initialScreen,
    getScreenAttributes,
  });

  const { offers } = useFlowOffers({
    attributes: flow.offers.attributes,
  });

  const { fetchOffers } = useFetchOffers({ offers, contentOffer: flow.offers.content });

  const { getEmailFlow } = useEmailFlow();
  const { redirectToLogin } = useRedirectToAskNebula();
  const { updateEmailConsentRequest, updateUserSettingsRequest } = useUserSettings();
  const { userPreferences, userResponses, updateUserPreferences, updateUserResponses } = useUser();

  const completeQuiz = async (email: string, productName: string) => {
    const { overriddenFunnelName } = flow.attributes;

    const emailFlow = getEmailFlow(flow.attributes.emailFlowAttributes);
    const emailFlowReports = flow.attributes.emailFlowAttributes?.reports;

    const funnelName = overriddenFunnelName || name;

    logger.info('EmailFlow: sending emailFlow to the backend with the orders query', {
      emailFlow,
      email,
      funnel: funnelName,
      nfb_flow: flow.name,
    });

    dispatch(
      createOrder({
        email,
        offerName: productName,
        quizName: funnelName,
        analyticsIds,
        emailFlow,
        reports: emailFlowReports,
      }),
    );
    fetchOffers();
  };

  const { defaultUserProfile } = flow.attributes;

  useEffect(() => {
    if (!defaultUserProfile) {
      return;
    }

    updateUserPreferences(defaultUserProfile);
  }, [defaultUserProfile, updateUserPreferences]);

  const updateUserData = useCallback(
    (dataKey: string, data: UserResponse) => {
      updateUserResponses({ [dataKey]: data });
      updateUserPreferences({ [dataKey]: data.value });
    },
    [updateUserResponses, updateUserPreferences],
  );

  const contextValue = {
    completeQuiz,
    userPreferences,
    updateUserSettings: updateUserSettingsRequest,
    updateUserEmailConsent: updateEmailConsentRequest,
    getScreenAttributes,
    next: goNext,
    extraNext: goExtraNext,
    back: goBack,
    link,
    offers,
    signIn: redirectToLogin,
    flowInitialScreenId: flow.initialScreen,
    updateDynamicAttributes,
    userResponses,
    updateUserData,
    fetchOffers,
  };

  return <QuizContext.Provider value={contextValue}>{children}</QuizContext.Provider>;
};

export const useQuizStep = (id: string) => {
  const {
    back,
    next,
    extraNext,
    getScreenAttributes,
    updateUserSettings,
    updateUserEmailConsent,
    userPreferences,
    completeQuiz,
    link,
    offers,
    signIn,
    updateDynamicAttributes,
    flowInitialScreenId,
    userResponses,
    updateUserData,
    fetchOffers,
  } = useContext(QuizContext);

  const screenAttributes = useMemo(() => getScreenAttributes(id), [id, getScreenAttributes]);
  const initialStep = screenAttributes.index;

  const analyticsHandlers = useAnalytics(screenAttributes.analytic);
  const { trackScreenRendered, trackRedirectToNextScreen } = useObservability();

  const [step, setStep] = useState(initialStep);

  usePostMessageToFunnelBuilderOnRouteChange(id);

  useEffect(() => {
    trackScreenRendered(flowInitialScreenId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const backStep = useCallback(() => {
    setStep((prev) => prev - 1);
    back(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const nextScreen = useCallback(
    async ({ value }: { value?: string | string[] } = {}) => {
      setStep((prev) => prev + 1);
      await next({ id, value });
      trackRedirectToNextScreen(flowInitialScreenId);

      if (screenAttributes.dataKey) {
        updateDynamicAttributes(screenAttributes.dataKey, id);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [next, id],
  );

  const extraNextScreen = useCallback(() => extraNext(id), [extraNext, id]);

  return {
    completeQuiz,
    back: backStep,
    next: nextScreen,
    extraNext: extraNextScreen,
    screenAttributes,
    getScreenAttributes,
    updateUserSettings,
    updateUserEmailConsent,
    userPreferences,
    step,
    offers,
    link,
    signIn,
    analyticsHandlers,
    userResponses,
    updateUserData,
    fetchOffers,
  };
};
